<template>
    <v-system-bar
        app
        v-if="isEnvironmentBarActive"
        :color="computedEnvironmentBarColor"
        height="32"
        style="justify-content: center; color: white; font-weight: bold; text-transform: uppercase"
    >
        {{ environmentBarText }} --- {{ currentUserRole.name }} --- {{ currentUser.firstName }}
        {{ currentUser.lastName }} ---
        {{ currentUser.email }}
    </v-system-bar>

    <!-- Mobile Navigation Drawer (for small screens) -->
    <BaseSidebar
        v-if="!display.lgAndUp.value"
        v-model="drawerOpen"
        temporary
        floating
        app
        location="left"
        width="256"
        class="mobile-nav-drawer"
        :rail="false"
        @update:modelValue="drawerOpen = $event"
        :items="mainNavItemsForSidebar"
        :disable-route-watcher="true"
    />

    <!-- Desktop Navigation Drawer (for large screens) -->
    <BaseSidebar
        v-if="display.lgAndUp.value"
        :model-value="true"
        :rail="sidebarBehaviorForLargeScreen === 'rail'"
        :items="desktopDrawerNavItemsForSidebar"
        permanent
        app
        location="left"
        :width="sidebarBehaviorForLargeScreen === 'rail' ? 72 : 256"
        :expand-on-hover="sidebarBehaviorForLargeScreen === 'rail'"
        class="main-app-sidebar"
    />

    <v-app-bar app flat class="navbar">
        <div class="navbar__content-area">
            <!-- Burger Menu Icon for smaller screens -->
            <v-app-bar-nav-icon
                v-if="showBurgerMenu"
                @click.stop="drawerOpen = !drawerOpen"
                class="mr-2"
            ></v-app-bar-nav-icon>

            <!-- Tabs for larger screens -->
            <v-tabs
                v-if="display.lgAndUp.value"
                :model-value="activeTab"
                align-tabs="start"
                class="navbar__tabs"
                slider-color="transparent"
                height="100%"
            >
                <v-tab
                    v-for="item in mainNavItems"
                    :key="item.name"
                    :value="item.name"
                    :to="item.path"
                    class="navbar__tab"
                    :class="{ 'navbar__tab--active': activeTab === item.name }"
                >
                    {{ item.title }}
                </v-tab>
            </v-tabs>
            <v-spacer></v-spacer>

            <div class="navbar__actions d-flex align-center">
                <!-- 1. Language pill -->
                <v-menu
                    v-model="languageMenuOpen"
                    :close-on-content-click="true"
                    location="bottom end"
                    offset-y
                    content-class="aq-menu-content"
                >
                    <template v-slot:activator="{ props: langProps }">
                        <div
                            v-bind="langProps"
                            class="navbar__language-pill d-flex align-center"
                            style="cursor: pointer"
                        >
                            <span class="mr-1">{{ getLanguageFlag(currentLanguage.code) }}</span>
                            <span class="text-body-2">
                                {{ currentLanguage.title }}
                            </span>
                        </div>
                    </template>
                    <v-list dense>
                        <v-list-subheader>{{ t('forms.labels.lang') }}</v-list-subheader>
                        <v-divider></v-divider>
                        <v-list-item
                            v-for="lang in availableLanguages"
                            :key="lang.code"
                            @click="selectLanguage(lang)"
                            :active="lang.code === currentLanguage.code"
                        >
                            <template #prepend>
                                <span class="mr-2">{{ getLanguageFlag(lang.code) }}</span>
                            </template>
                            <v-list-item-title>{{ lang.title }}</v-list-item-title>
                            <template #append v-if="lang.code === currentLanguage.code">
                                <AQIcon name="mdi-check" size="small" />
                            </template>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- 2. Notification (plain, with badge) -->
                <v-badge
                    v-if="props.showNotifications"
                    :content="unacknowledgedCount"
                    :model-value="unacknowledgedCount > 0"
                    color="error"
                    dot
                    overlap
                >
                    <AQButton
                        class="navbar__notification-icon"
                        variant="text"
                        @click="goToNotifications"
                    >
                        <AQIcon size="large" name="mdi-bell-outline" />
                    </AQButton>
                </v-badge>

                <!-- 3. User menu with notification badge on avatar -->
                <div class="navbar__user-menu d-flex align-center">
                    <v-menu
                        v-model="userMenuOpen"
                        :close-on-content-click="true"
                        location="bottom end"
                        offset-y
                        content-class="aq-menu-content"
                    >
                        <template v-slot:activator="{ props: avatarProps }">
                            <div
                                v-bind="avatarProps"
                                class="navbar__avatar-button d-flex align-center"
                                style="cursor: pointer"
                            >
                                <span class="initial-text">{{ clientInitial }}</span>
                            </div>
                        </template>
                        <v-list dense class="user-dropdown-menu">
                            <template v-for="item in userMenuItems" :key="item.id">
                                <v-list-item
                                    v-if="item.isHeader"
                                    class="user-dropdown-menu__header"
                                    :ripple="false"
                                    :value="item.id"
                                >
                                    <v-list-item-title class="font-weight-bold">
                                        {{ item.dynamicText() }}
                                    </v-list-item-title>
                                </v-list-item>
                                <v-divider
                                    v-else-if="item.isDivider"
                                    :key="'divider-' + item.id"
                                ></v-divider>
                                <v-list-item
                                    v-else-if="item.hasSubmenu"
                                    :value="item.id"
                                    :disabled="item.disabled"
                                    @click.stop
                                >
                                    <template #prepend v-if="item.icon">
                                        <AQIcon :name="item.icon" :color="Colors.aquassistBlack" />
                                    </template>
                                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                                    <template #append>
                                        <AQIcon name="mdi-chevron-right" size="small" />
                                    </template>
                                    <v-menu
                                        activator="parent"
                                        location="end"
                                        :close-on-content-click="true"
                                        open-on-hover
                                        content-class="aq-menu-content"
                                    >
                                        <v-list dense>
                                            <v-list-item
                                                v-for="subItem in item.submenuItems"
                                                :key="subItem.id"
                                                @click="handleSubmenuItemClick(subItem)"
                                            >
                                                <template #prepend v-if="subItem.icon">
                                                    <AQIcon
                                                        :name="subItem.icon"
                                                        :color="Colors.aquassistBlack"
                                                    />
                                                </template>
                                                <v-list-item-title>{{
                                                    subItem.text
                                                }}</v-list-item-title>
                                                <template #append v-if="subItem.isExternal">
                                                    <AQIcon name="mdi-open-in-new" size="small" />
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-list-item>
                                <v-list-item
                                    v-else
                                    @click="handleUserMenuItemClick(item.action)"
                                    :disabled="item.disabled"
                                    :value="item.id"
                                    :class="{ 'logout-item': item.id === 'logout' }"
                                >
                                    <template #prepend v-if="item.icon">
                                        <AQIcon
                                            :name="item.icon"
                                            :color="
                                                item.id === 'logout'
                                                    ? Colors.aquassistRed
                                                    : Colors.aquassistBlack
                                            "
                                        />
                                    </template>
                                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-list>
                    </v-menu>
                </div>
            </div>
        </div>
    </v-app-bar>

    <AQModal
        v-model="isFeedbackModalOpen"
        :title="t('feedback.title')"
        @close="isFeedbackModalOpen = false"
        @confirm="handleConfirmModal"
        :is-confirm-disabled="isConfirmDisabled"
    >
        <AQTextField
            name="feedbackInput"
            v-model="feedbackInput"
            :label="t('forms.labels.subject')"
            required
        />
        <AQTextArea
            name="feedbackMessage"
            v-model="feedbackMessage"
            :label="t('forms.labels.message')"
            required
        />
    </AQModal>
</template>

<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useRoute, useRouter } from 'vue-router';
    import { useDisplay, useLocale } from 'vuetify';
    import { useStore } from 'vuex';
    import BaseSidebar from './BaseSidebar.vue';
    import type { NavItem } from '@/interfaces/navigation/NavItem.types';
    import { Colors } from '@/public/styles/utils/variables';
    import { LOCALES } from '@/locales/i18n';
    import AQModal from '@/components/shared/containers/AQModal.vue';
    import AQTextField from '@/components/shared/forms/inputs/AQTextField.vue';
    import AQTextArea from '@/components/shared/forms/inputs/AQTextArea.vue';
    import $rq from '@/api/api';
    import { toast } from 'vue3-toastify';
    import { phoneCountries } from '@/aquassist-shared/constants/phoneCountries';
    import { regexes } from '@/utilities/utils';
    import services from '@/routes/services';
    import laboratory from '@/routes/laboratory';
    import crm from '@/routes/crm';
    import administration from '@/routes/administration';
    import { getNavItemMeta } from '@/store/modules/navigation';
    import AQButton from '../shared/forms/inputs/AQButton.vue';
    import AQIcon from '../shared/interactions/AQIcon.vue';
    import { roles } from '@/constants/roles';

    interface NavbarProps {
        showNotifications?: boolean;
    }
    const props = withDefaults(defineProps<NavbarProps>(), {
        showNotifications: true,
    });

    const { locale, t } = useI18n();
    const display = useDisplay();
    const { current: vuetifyLocale } = useLocale();
    const route = useRoute();
    const store = useStore();
    const router = useRouter();
    const hasPermission = computed(() => store.getters.hasPermission);

    const currentUser = computed(() => store.getters['getCurrentUser']);
    const currentTeamName = computed<string | null>(() => store.getters['currentTeamName'] || null);
    const currentUserRole = computed(() => {
        const user = currentUser.value;
        if (!user || !Array.isArray(user.roles) || !user.roles[0]) {
            return { key: 'guest' };
        }
        return user?.roles?.[0];
    });
    type SubmenuItem = {
        id: string;
        text: string;
        icon?: string;
        action: () => void;
        isExternal?: boolean;
    };

    type UserMenuItem =
        | {
              id: string;
              isHeader: true;
              dynamicText: () => string;
              text?: never;
              action?: never;
              isDivider: boolean;
              disabled?: boolean;
              icon?: false;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isHeader?: false | undefined;
              text: string;
              action: (() => void) | (() => Promise<void>) | string;
              dynamicText?: never;
              isDivider: boolean;
              disabled?: boolean;
              icon?: string;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isDivider: true;
              text?: never;
              dynamicText?: never;
              action?: never;
              isHeader: false;
              disabled: false;
              icon?: never;
              hasSubmenu?: never;
          }
        | {
              id: string;
              isHeader?: false;
              text: string;
              hasSubmenu: true;
              submenuItems: SubmenuItem[];
              dynamicText?: never;
              isDivider: false;
              disabled?: boolean;
              icon?: string;
              action?: never;
          };

    interface Language {
        code: string;
        title: string;
    }

    const helpCenterSubmenuItems: SubmenuItem[] = [
        {
            id: 'webinar',
            text: t('helpCenter.webinar'),
            icon: 'mdi-video-outline',
            isExternal: false,
            action: () => {
                router.push({ name: 'movies' });
            },
        },
        {
            id: 'devices-documentation',
            text: t('helpCenter.devicesDocumentation'),
            icon: 'mdi-file-document-outline',
            isExternal: false,
            action: () => {
                router.push({ name: 'devicesDocumentation' });
            },
        },
        {
            id: 'aq-community',
            text: t('helpCenter.community'),
            icon: 'mdi-web',
            isExternal: true,
            action: () => {
                window.open('https://community.aquassist.com/', '_blank', 'noopener');
            },
        },
    ];

    if (
        store.getters.hasRole(roles.MANAGER.value) ||
        store.getters.hasRole(roles.ADMINISTRATOR.value)
    ) {
        helpCenterSubmenuItems.push({
            id: 'aquassist-support',
            text: t('helpCenter.support'),
            icon: 'mdi-headset',
            isExternal: true,
            action: () => {
                window.open(
                    'https://aquassist.atlassian.net/servicedesk/customer/portals',
                    '_blank',
                    'noopener',
                );
            },
        });
    }

    const userMenuItems = computed<UserMenuItem[]>(() => [
        {
            id: 'user-email',
            dynamicText: () => String(userEmail.value ?? ''),
            isHeader: true,
            isDivider: false,
            disabled: false,
        },
        {
            id: 'divider-after-email',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'profile',
            text: t('profile.title'),
            icon: 'mdi-account-outline',
            action: () => {
                router.push({ name: 'profile' });
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'switch-team',
            text: t('teams.switchTeam'),
            icon: 'mdi-account-group-outline',
            action: () => {
                router.push({ name: 'teams' });
            },
            isDivider: false,
            disabled: !currentTeamName.value,
        },
        {
            id: 'divider-after-switch-team',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'release-notes',
            text: t('administration.release'),
            icon: 'mdi-notebook-outline',
            action: () => {
                //router.push({ name: 'ReleaseNotes' });
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'divider-after-release-notes',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'send-feedback',
            text: t('feedback.title'),
            icon: 'mdi-message-text-outline',
            action: () => {
                isFeedbackModalOpen.value = true;
            },
            isDivider: false,
            disabled: false,
        },
        {
            id: 'help-center',
            text: t('helpCenter.title'),
            icon: 'mdi-help-circle-outline',
            hasSubmenu: true,
            isDivider: false,
            disabled: false,
            submenuItems: helpCenterSubmenuItems,
        },
        {
            id: 'divider-before-logout',
            isDivider: true,
            isHeader: false,
            disabled: false,
        },
        {
            id: 'logout',
            text: t('logout.pageName'),
            icon: 'mdi-logout-variant',
            action: async () => {
                await store.dispatch('logout');
                router.push('/');
            },
            isDivider: false,
            disabled: false,
        },
    ]);

    const drawerOpen = ref(false);
    const showBurgerMenu = computed(() => {
        // Burger icon is only for small screens
        return !display.lgAndUp.value;
    });

    const sidebarBehaviorForLargeScreen = computed<'expanded' | 'rail'>(
        () => store.getters['navigation/getSidebarBehaviorForLargeScreen'],
    );

    const isEnvironmentBarActive = ref(true);
    const environmentBarText = ref('');
    const environmentBarColorClass = ref('');

    const appMode = process.env.VUE_APP_MODE;

    if (appMode === 'demo') {
        environmentBarText.value = 'DEMO';
        environmentBarColorClass.value = 'environment-bar--demo';
    } else if (appMode === 'qa') {
        environmentBarText.value = 'QA';
        environmentBarColorClass.value = 'environment-bar--qa';
    } else if (appMode === 'dev') {
        environmentBarText.value = 'DEV';
        environmentBarColorClass.value = 'environment-bar--dev';
    } else {
        if (process.env.NODE_ENV === 'development') {
            environmentBarText.value = 'DEV';
            environmentBarColorClass.value = 'environment-bar--dev';
        } else {
            isEnvironmentBarActive.value = false;
        }
    }

    const computedEnvironmentBarColor = computed(() => {
        switch (environmentBarColorClass.value) {
            case 'environment-bar--dev':
                return Colors.environmentBarDev;
            case 'environment-bar--demo':
                return Colors.environmentBarDemo;
            case 'environment-bar--qa':
                return Colors.environmentBarQa;
            default:
                return 'none';
        }
    });

    const activeTab = computed<string | null>(() => {
        if (typeof route.path !== 'string') {
            return null;
        }
        const currentPath = route.path;
        // Client site detail routes (clientId + siteId params) should highlight the "Sites" tab
        const { clientId, siteId } = route.params;
        if (clientId && siteId) {
            const sitesItem = mainNavItems.value.find(
                (item) => item.name === 'sites' || item.path === '/sites',
            );
            return sitesItem?.name ?? null;
        }
        const pathSegments = currentPath.split('/');

        if (pathSegments.length < 2 || pathSegments[1] === '') {
            const rootItem = mainNavItems.value.find((item) => item.path === '/');
            return rootItem?.name ?? null;
        }

        const basePathForTabMatching = '/' + pathSegments[1];
        const foundItem = mainNavItems.value.find((item) => item.path === basePathForTabMatching);
        return foundItem?.name ?? null;
    });

    //ANY TO COMBINE BOTH NAVBAR AND SIDEBAR NEEDED
    //eslint-disable-next-line
    const mainNavItemsForSidebar = computed(() => mainNavItems.value as any);

    //eslint-disable-next-line
    const desktopDrawerNavItemsForSidebar = computed(() => desktopDrawerNavItems.value as any);

    const clientMainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getClientNavItems'] || [];
        const activeSubItems = itemsFromStore.map((item: NavItem) => {
            const filteredSubItems =
                item.subItems?.filter(
                    (subItem: NavItem) =>
                        subItem.allowedRoles?.includes('all') ||
                        subItem.allowedRoles?.includes(currentUserRole.value?.key),
                ) || [];
            return {
                ...item,
                subItems: filteredSubItems,
                title: t(`${item.name?.toLowerCase()}.pageName`),
            };
        });
        return activeSubItems;
    });

    const clientSiteMainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getClientSiteNavItems'] || [];
        // clientSite nav items should be filtered based on the role and just subItems should be considered
        const ss = itemsFromStore.map((item: NavItem) => {
            const filteredSubItems =
                item.subItems?.filter((subItem: NavItem) => {
                    const roleAllowed =
                        subItem.allowedRoles?.includes('all') ||
                        subItem.allowedRoles?.includes(currentUserRole.value.key);
                    if (!roleAllowed) return false;

                    // Cacher Devices menu  si le user n'a pas la permission de voir les devices
                    if (subItem.name === 'siteDevices') {
                        const canViewDevices = hasPermission.value('sites.devices.view');
                        return canViewDevices;
                    }
                    return true;
                }) || [];
            return {
                ...item,
                title: t(`${item.name?.toLowerCase()}.pageName`),
                subItems: filteredSubItems,
            };
        });
        return ss;
    });

    const desktopDrawerNavItems = computed<NavItem[]>(() => {
        if (!activeTab.value || !mainNavItems.value) {
            return [];
        }
        const currentPath = route.path;
        // Hide sidebar for notifications page
        if (currentPath === '/notifications') {
            return [];
        }
        // Special case: client site routes should have a special sidebar
        const { clientId, siteId } = route.params;
        if (regexes.clientSite.test(currentPath) && clientId && siteId) {
            // Create special sidebar for client site routes
            return clientSiteMainNavItems.value;
        }
        if (regexes.client.test(currentPath) && clientId) {
            // Create special sidebar for client routes
            return clientMainNavItems.value;
        }
        return mainNavItems.value || [];
    });

    const fieldServiceMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(services.children || [], services.path);
    });
    const laboratoryMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(laboratory.children || [], laboratory.path);
    });
    const adminMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(administration.children || [], administration.path);
    });
    const crmMainNavItems = computed<NavItem[]>(() => {
        return getNavItemMeta(crm.children || [], crm.path);
    });

    const featureFlagGetters: Record<string, string> = {
        EVENT_MANAGEMENT: 'showEventManagementByFeature',
        CONTRACT: 'showContractByFeature',
        GLOBAL_DASHBOARD: 'showGlobalDashboardByFeature',
        CRM: 'showCrmByFeature',
    };

    const isFeatureActive = (featureFlag?: string): boolean => {
        if (!featureFlag) return true;
        const getter = featureFlagGetters[featureFlag];
        if (!getter) return true;
        return !!store.getters[getter];
    };

    const isRoleAllowed = (item: NavItem): boolean => {
        if (item.superAdmin) return store.getters.superAdmin === true;
        return (
            !!item.allowedRoles?.includes('all') ||
            !!item.allowedRoles?.includes(currentUserRole.value?.key)
        );
    };

    const mainNavItems = computed<NavItem[]>(() => {
        const itemsFromStore = store.getters['navigation/getMainNavItems'] || [];
        if (!currentTeamName.value) return [];

        const items: NavItem[] = itemsFromStore
            .filter((item: NavItem) => {
                // If the feature flag is inactive, hide the item
                if (!isFeatureActive(item.featureFlag)) return false;
                // If item.allowedRoles contains 'all', include the item
                if (item.allowedRoles?.includes('all')) return true;
                // If item.allowedRoles contains the current role, include the item
                if (item.allowedRoles?.includes(currentUserRole.value?.key)) return true;
                // If any subItem is allowed, include the item
                if (item.subItems?.some(isRoleAllowed)) {
                    return true;
                }
                // Otherwise, exclude the item
                return false;
            })
            .map((item: NavItem) => {
                // Filter subItems
                // Recursively filter subItems
                function filterSubItems(subItems: NavItem[]): NavItem[] {
                    return (
                        subItems
                            ?.filter(
                                (subItem: NavItem) =>
                                    isFeatureActive(subItem.featureFlag) &&
                                    (isRoleAllowed(subItem) ||
                                        (subItem.subItems && subItem.subItems.some(isRoleAllowed))),
                            )
                            .map((subItem: NavItem) => ({
                                ...subItem,
                                subItems: subItem.subItems ? filterSubItems(subItem.subItems) : [],
                            })) || []
                    );
                }
                const filteredSubItems = item.subItems ? filterSubItems(item.subItems) : [];

                return {
                    ...item,
                    title: t(`${item.name?.toLowerCase()}.pageName`),
                    subItems: filteredSubItems,
                };
            });
        const currentPath = route.path;
        const { clientId, siteId } = route.params;
        if (regexes.clientSite.test(currentPath) && clientId && siteId && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('sitePage.pageName'),
                isDivider: true,
            };
            return [...items, divider, ...clientSiteMainNavItems.value];
        } else if (regexes.client.test(currentPath) && clientId && !display.lgAndUp.value) {
            // Create special sidebar for client routes
            const divider: NavItem = {
                title: t('clientPage.pageName'),
                isDivider: true,
            };
            return [...items, divider, ...clientMainNavItems.value];
        } else if (regexes.fieldService.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('servicesPage.services'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...fieldServiceMainNavItems.value];
        } else if (regexes.laboratory.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('pageToDisplay.laboratory'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...laboratoryMainNavItems.value];
        } else if (regexes.crm.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('crm.pageName'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...crmMainNavItems.value];
        } else if (regexes.administration.test(currentPath) && !display.lgAndUp.value) {
            const divider: NavItem = {
                title: t('administration.pageName'),
                isDivider: true,
            };
            const _items = items.map((item) => {
                return {
                    ...item,
                    hasChildren: false,
                    subItems: [],
                };
            });
            return [..._items, divider, ...adminMainNavItems.value];
        }
        return items;
    });

    const userEmail = computed<string | null>(() => store.state.auth.currentUser?.email || null);
    const clientInitial = computed(() =>
        currentTeamName.value ? currentTeamName.value[0].toUpperCase() : '',
    );

    const languageMenuOpen = ref(false);

    const availableLanguages = computed<Language[]>(() =>
        LOCALES.map((langCode) => ({
            code: langCode,
            title: langCode.charAt(0).toUpperCase() + langCode.slice(1),
        })),
    );

    const currentLanguage = computed<Language>(() => {
        return (
            availableLanguages.value.find((lang) => lang.code === store.getters.userLang) ||
            availableLanguages.value[0]
        );
    });

    const selectLanguage = (lang: Language) => {
        store.dispatch('setLang', lang.code);
        locale.value = lang.code;
        vuetifyLocale.value = lang.code;
        languageMenuOpen.value = false;
    };
    const userMenuOpen = ref(false);

    const unacknowledgedCount = computed(() => store.getters['notifications/unacknowledgedCount']);

    const goToNotifications = () => {
        router.push({ name: 'notifications' });
    };

    const handleSubmenuItemClick = (subItem: SubmenuItem) => {
        subItem.action();
        userMenuOpen.value = false;
    };

    const handleUserMenuItemClick = (actionProperty?: (() => void) | string) => {
        if (typeof actionProperty === 'function') {
            actionProperty();
        } else {
            if (typeof actionProperty === 'string') {
                router.push(actionProperty);
            }
        }
    };

    const isFeedbackModalOpen = ref(false);
    const feedbackInput = ref('');
    const feedbackMessage = ref('');

    const isConfirmDisabled = computed(() => !feedbackInput.value || !feedbackMessage.value);

    const handleConfirmModal = async (): Promise<void> => {
        const bodyParams = {
            title: feedbackInput.value,
            message: feedbackMessage.value,
            url: window.location.href,
        };

        await $rq.sendFeedBack(bodyParams);
        feedbackInput.value = '';
        feedbackMessage.value = '';
        toast.success(t('feedback.success'));
    };

    const getLanguageFlag = (langCode: string): string => {
        const specialCases: Record<string, string> = {
            en: 'GB',
        };

        const countryCode = specialCases[langCode] || langCode.toUpperCase();
        const country = phoneCountries.find((c) => c.iso2 === countryCode);
        return country?.flag || '🌐';
    };
</script>
